/**
 * meetingProvider — shared detection of the video-meeting platform for a
 * calendar event, from a join URL and/or a provider hint.
 *
 * Single source of truth for the "is this Google Meet / Teams / Zoom" logic
 * that both the CREATE-event editor (``CalendarCreateEventPreview``, which has
 * only a provider hint) and the event DETAIL pane (``CalendarEventPane``, which
 * has the actual ``conference_url``) need. Lives in ``@ando/ui`` — the leaf
 * package both the UI kit and ``apps/client`` can import (the "zero-dep on
 * @ando/core" leaf rule forbids putting it in @ando/core).
 */

export type MeetingProviderKind = "google_meet" | "teams" | "zoom" | "generic";

/**
 * Detect the meeting platform from a join URL and/or a provider hint
 * (``"google"`` / ``"outlook"``). URL patterns win; the hint is the fallback
 * used at create time before any link exists. Unknown ⇒ ``"generic"``.
 */
export function detectMeetingProvider(opts: {
  url?: string | null;
  provider?: string | null;
}): MeetingProviderKind {
  const u = (opts.url ?? "").toLowerCase();
  const p = (opts.provider ?? "").toLowerCase();
  if (
    u.includes("teams.microsoft") ||
    u.includes("teams.live") ||
    p.includes("outlook") ||
    p.includes("teams")
  ) {
    return "teams";
  }
  if (u.includes("meet.google") || u.includes("hangouts") || p.includes("google")) {
    return "google_meet";
  }
  if (u.includes("zoom")) return "zoom";
  return "generic";
}

/**
 * Brand display name — a proper noun, intentionally NOT localized (wrapping
 * "Google Meet" in the message catalog would be an anti-pattern). Returns
 * ``null`` for the generic case so the caller substitutes a translated
 * fallback (e.g. "Videochiamata").
 */
export function meetingProviderBrand(kind: MeetingProviderKind): string | null {
  switch (kind) {
    case "google_meet":
      return "Google Meet";
    case "teams":
      return "Microsoft Teams";
    case "zoom":
      return "Zoom";
    default:
      return null;
  }
}
